import { httpsCallable } from 'firebase/functions';
import { getFbFunctions } from './development';
import { updateDoc, doc, getDoc, getFirestore, setDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { FirebaseError, getApp } from 'firebase/app';
import type { checkDomainResponse, KeycloakSSOConfig, KeycloakSSODomain } from './types';
import type { CustomHostnameGetResponse } from 'cloudflare/resources/custom-hostnames/custom-hostnames.mjs';
import { disableDomainCache } from '../assets/js/helpers/DomainUtils.ts';
import type { IdTokenResult } from 'firebase/auth';

const authorizedDomainsMatch = (customDomain?: string) => {
  if (import.meta.env.VITE_SENTRY_ENV == 'production') {
    return `+(${import.meta.env.VITE_HOSTNAME}|rackmanage.net${customDomain ? `|${customDomain}` : ''})`;
  } else {
    return `+(localhost|${import.meta.env.VITE_HOSTNAME}|rackmanage.net|*.${import.meta.env.VITE_DEV_DOMAIN}${customDomain ? `|${customDomain}` : ''})`;
  }
};

export async function getAuthorizedDomains() {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  let domains = authorizedDomainsMatch();
  if (this.team != null) {
    const docRef = doc(db, 'teams', this.team);
    try {
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        if (docSnap.data().customDomain == null) {
          this.authorizedDomains = domains;
          return domains;
        } else {
          domains = authorizedDomainsMatch(docSnap.data().customDomain);
          this.authorizedDomains = domains;
          return domains;
        }
      } else {
        this.authorizedDomains = domains;
        return domains;
      }
    } catch (err) {
      if (err instanceof FirebaseError && err.code == 'permission-denied') {
        // If we failed due to SSO enforcement, see if we can get the sso doc
        try {
          const ssoRef = doc(db, 'teams', this.team, 'private', 'sso');
          const ssoDoc = await getDoc(ssoRef);
          if (ssoDoc.exists() && ssoDoc.data().enforceSso) {
            const functions = getFbFunctions(fbApp);
            const getDomains = httpsCallable(functions, 'domains-getCustomDomain');
            const result = await getDomains({ teamId: this.team }) as { data: string };

            if (result.data) {
              domains = authorizedDomainsMatch(result.data);
              this.authorizedDomains = domains;
            } else {
              this.authorizedDomains = domains;
            }
          } else {
            this.authorizedDomains = domains;
          }
        } catch {
          this.authorizedDomains = domains;
        }
      } else {
        this.authorizedDomains = domains;
      }
      return domains;
    }
  } else {
    // If user logged in with SSO and has a token, see if JIT is enabled and get custom domain if so
    try {
      const tokenResult = await this.user?.getIdTokenResult() as IdTokenResult;
      /* @ts-expect-error sign_in_attributes */
      const tenantId = tokenResult.claims?.firebase?.sign_in_attributes?.tenant?.id ?? null;
      if (tokenResult && tenantId) {
        const ssoRef = doc(db, 'teams', tenantId, 'private', 'sso');
        const ssoDoc = await getDoc(ssoRef);
        if (ssoDoc.exists() && ssoDoc.data()?.jitEnabled) {
          const functions = getFbFunctions(fbApp);
          const getDomains = httpsCallable(functions, 'domains-getCustomDomain');
          const result = await getDomains({ teamId: tenantId }) as { data: string };

          if (result.data) {
            domains = authorizedDomainsMatch(result.data);
            this.authorizedDomains = domains;
          } else {
            this.authorizedDomains = domains;
          }
        }
      }
    } catch {
      this.authorizedDomains = domains;
    }

    this.authorizedDomains = domains;
    return domains;
  }
}

export async function checkDomain(domain: string) {
  // If the domain is already checked and valid, and was checked less than half an hour ago, return true
  if (
    this.checkedDomain != null &&
    this.checkedDomain.domain == domain &&
    this.checkedDomain.data &&
    Date.now() - this.checkedDomain.timestamp < 1800000 &&
    !disableDomainCache()
  ) {
    return true;
  } else {
    try {
      const result = await fetch(
        import.meta.env.VITE_API_URL + 'check-domain?domain=' + domain,
      );

      const jsonResult = await result.json() as checkDomainResponse;

      this.checkedDomain = {
        domain: domain,
        data: jsonResult.result,
        timestamp: Date.now(),
      };
      return jsonResult.result;
    } catch (error) {
      console.warn('checkDomain: Error checking domain', error);
      return false;
    }
  }
}

export async function setCustomDomain(domain: string): Promise<boolean> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);


  if (this.team == null) {
    return false;
  }

  try {
    const setDomain = httpsCallable(functions, 'domains-setCustomDomain');
    const data = {
      domain: domain,
      teamId: this.team,
    };
    const result = await setDomain(data) as { data: boolean };
    return result.data;
  } catch {
    return false;
  }
}

export async function getCustomDomain() {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.team != null) {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      if (docSnap.data().customDomain == null) {
        return null;
      } else {
        return docSnap.data().customDomain;
      }
    } else {
      return null;
    }
  } else {
    return null;
  }
}

export async function getCloudflareRecords(): Promise<CustomHostnameGetResponse | null> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);

  if (this.team == null) {
    return null;
  }

  const getRecords = httpsCallable(
    functions,
    'domains-checkCloudflareHostname',
  );
  const result = await getRecords({ teamId: this.team }) as { data: CustomHostnameGetResponse | false };

  if (result.data) {
    return result.data;
  } else {
    return null;
  }
}

export async function deleteCustomDomain(): Promise<boolean> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.user && this.team != null) {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().admins && docSnap.data().admins.includes(this.user.uid)) {
      // Check if user has cloudflare_id set
      if (docSnap.data().cloudflare_id == null) {
        // If not, just delete the custom domain
        try {
          await updateDoc(doc(db, 'teams', this.team), {
            customDomain: null,
          });
          return true;
        } catch {
          return false;
        }
      } else {
        // If so, delete the custom domain and call the cloudflare function to delete the DNS records
        try {
          const deleteCloudflareRecords = httpsCallable(functions, 'domains-deleteCloudflareHostname' );
          const result = await deleteCloudflareRecords({ teamId: this.team }) as { data: { result: boolean } };
          if (result.data.result) {
            return true;
          } else {
            return false;
          }
        } catch {
          return false;
        }
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export async function getAuthProviders() {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.team != null) {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      if (docSnap.data().authProviders == null) {
        return null;
      } else {
        return docSnap.data().authProviders;
      }
    } else {
      return null;
    }
  } else {
    return null;
  }
}

export async function setAuthProviders(providers: string[]): Promise<boolean> {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.team != null) {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      try {
        await updateDoc(doc(db, 'teams', this.team), {
          authProviders: providers,
        });
        return true;
      } catch {
        return false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export type KeycloakSSOConfigResult =
  | { success: true; message: string; sso: KeycloakSSOConfig }
  | { success: false; message: string; error?: string };

export type KeycloakSSOWizardResult =
  | { success: true; message: string; wizard: {
    link: string;
    user: string;
    redirect: string;
  } }
  | { success: false; message: string; error?: string };

export type KeycloakSSODomainResult =
  | { success: true; message: string; domain?: KeycloakSSODomain }
  | { success: false; message: string; error?: string };

/** Get Keycloak SSO config for the team (organization, domains, JIT/enforce). */
export async function getKeycloakSSOConfig(): Promise<KeycloakSSOConfig | null> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  if (!this.user || this.team == null) return null;
  try {
    const fn = httpsCallable<{ teamId?: string }, KeycloakSSOConfigResult>(functions, 'sso-getSSOProvider');
    const result = await fn({ teamId: this.team });
    const data = result.data as KeycloakSSOConfigResult;
    if (data.success && data.sso) return data.sso;
    return null;
  } catch {
    return null;
  }
}

/** Create Keycloak SSO org for the team. Fails if SSO already exists. */
export async function createKeycloakSSO(domains?: string[]): Promise<KeycloakSSOConfigResult> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  if (!this.user || this.team == null) {
    return { success: false, message: 'Not authenticated or no team' };
  }
  try {
    const fn = httpsCallable<{ teamId?: string; domains?: string[] }, KeycloakSSOConfigResult>(functions, 'sso-createSSOProvider');
    const result = await fn({ teamId: this.team, domains });
    return result.data as KeycloakSSOConfigResult;
  } catch (e) {
    const err = e as { message?: string };
    return { success: false, message: err.message ?? 'Failed to create SSO' };
  }
}

/** Update Keycloak SSO org (e.g. domains). */
export async function updateKeycloakSSO(domains?: string[]): Promise<KeycloakSSOConfigResult> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  if (!this.user || this.team == null) {
    return { success: false, message: 'Not authenticated or no team' };
  }
  try {
    const fn = httpsCallable<{ teamId?: string; domains?: string[] }, KeycloakSSOConfigResult>(functions, 'sso-updateSSOProvider');
    const result = await fn({ teamId: this.team, domains });
    return result.data as KeycloakSSOConfigResult;
  } catch (e) {
    const err = e as { message?: string };
    return { success: false, message: err.message ?? 'Failed to update SSO' };
  }
}

/** Verify a domain for the team's Keycloak org. */
export async function verifyKeycloakSSODomain(domain: string): Promise<KeycloakSSODomainResult> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  if (!this.user || this.team == null) {
    return { success: false, message: 'Not authenticated or no team' };
  }
  try {
    const fn = httpsCallable<{ teamId?: string; domain: string }, KeycloakSSODomainResult>(functions, 'sso-verifySSODomain');
    const result = await fn({ teamId: this.team, domain });
    return result.data as KeycloakSSODomainResult;
  } catch (e) {
    const err = e as { message?: string };
    return { success: false, message: err.message ?? 'Failed to verify domain' };
  }
}

/** Patch JIT and enforce SSO settings only. */
export async function updateSSOSettings(settings: {
  jitEnabled: boolean;
  jitDefaultRole: 'Viewer' | 'Editor' | 'Admin';
  enforceSso: boolean;
}): Promise<{ success: boolean; message: string }> {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);
  if (!this.user || this.team == null) {
    return { success: false, message: 'Not authenticated or no team' };
  }
  try {
    const ssoRef = doc(db, 'teams', this.team, 'private', 'sso');

    await setDoc(ssoRef, {
      jitEnabled: settings.jitEnabled,
      jitDefaultRole: settings.jitDefaultRole,
      enforceSso: settings.enforceSso,
    });

    return { success: true, message: 'SSO settings updated' };
  } catch (e) {
    const err = e as { message?: string };
    return { success: false, message: err.message ?? 'Failed to update SSO settings' };
  }
}

/** Get Keycloak wizard URL for the team (redirect to configure SSO). */
export async function getKeycloakSSOWizard(): Promise<KeycloakSSOWizardResult> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  if (!this.user || this.team == null) {
    return { success: false, message: 'Not authenticated or no team' };
  }
  try {
    const fn = httpsCallable<{ teamId?: string }, KeycloakSSOWizardResult>(functions, 'sso-getSSOWizard');
    const result = await fn({ teamId: this.team });
    return result.data as KeycloakSSOWizardResult;
  } catch (e) {
    const err = e as { message?: string };
    return { success: false, message: err.message ?? 'Failed to get wizard URL' };
  }
}

export async function autoJoinSSO(teamId: string) {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  if (!this.user) {
    return { success: false, message: 'Not authenticated' };
  }
  try {
    const fn = httpsCallable<{ teamId: string }, boolean>(functions, 'sso-autoJoinSSO');
    const result = await fn({ teamId });
    return result.data;
  } catch (e) {
    const err = e as { message?: string };
    return { success: false, message: err.message ?? 'Failed to auto join SSO' };
  }
}

export async function bypassSSO(userId: string) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);
  if (!this.user || this.team == null) {
    return { success: false, message: 'Not authenticated or no team' };
  }
  try {
    const ssoRef = doc(db, 'teams', this.team);
    await updateDoc(ssoRef, {
      ssoBypassUsers: arrayUnion(userId),
    });
    await this.getTeam(this.team);
    return { success: true, message: 'SSO bypassed' };
  } catch (e) {
    const err = e as { message?: string };
    return { success: false, message: err.message ?? 'Failed to bypass SSO' };
  }
}

export async function unbypassSSO(userId: string) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);
  if (!this.user || this.team == null) {
    return { success: false, message: 'Not authenticated or no team' };
  }
  try {
    const ssoRef = doc(db, 'teams', this.team);
    await updateDoc(ssoRef, {
      ssoBypassUsers: arrayRemove(userId),
    });
    await this.getTeam(this.team);
    return { success: true, message: 'SSO unbypassed' };
  } catch (e) {
    const err = e as { message?: string };
    return { success: false, message: err.message ?? 'Failed to unbypass SSO' };
  }
}

export async function getIdps() {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  if (!this.user || this.team == null) {
    return { success: false, message: 'Not authenticated or no team' };
  }
  try {
    const fn = httpsCallable(functions, 'sso-getSSOIdps');
    const result = await fn({ teamId: this.team }) as { data: { success: boolean; message: string; idps: {
      alias: string;
      enabled: boolean;
      name: string;
      providerId: string;
    }[] } };
    return { success: result.data.success, message: result.data.message, idps: result.data.idps };
  } catch (e) {
    const err = e as { message?: string };
    return { success: false, message: err.message ?? 'Failed to get IDPs' };
  }
}
